import { useState } from "react";

import { routes } from "utilities/routes";

import { BTAlert } from "commonComponents/btWrappers/BTAlert/BTAlert";
import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTIconUpsellCrown } from "commonComponents/btWrappers/BTIcon";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { LinkButton } from "commonComponents/btWrappers/LinkButton/LinkButton";
import "commonComponents/entity/marketing/PackageUpsell/PackageUpsell.less";
import { type IPackageUpsellProps } from "commonComponents/entity/marketing/PackageUpsell/PackageUpsell.types";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

export const PackageUpsell: React.FC<IPackageUpsellProps> = ({
    contentClassName,
    upgradeRequiredInfo,
    title,
    imageSrc,
    children,
}) => {
    const { requiredPackage, userCanUpgrade } = upgradeRequiredInfo;

    const [showBanner, setShowBanner] = useState(false);

    const getUpsellButton = () => {
        if (userCanUpgrade) {
            return (
                <LinkButton
                    data-testid="upsellPackage"
                    type="primary"
                    className="PackageUpsell--UpgradeButton"
                    to={routes.subscriptionManager.getSettingsLink(false, false)}
                    icon={<BTIconUpsellCrown />}
                >
                    Upgrade to {requiredPackage}
                </LinkButton>
            );
        } else {
            return (
                <BTButtonDeprecated
                    data-testid="upsellPackage"
                    className="PackageUpsell--UpgradeButton"
                    type="primary"
                    onClick={() => {
                        if (!userCanUpgrade) {
                            setShowBanner(true);
                        }
                    }}
                    disabled={showBanner}
                    icon={<BTIconUpsellCrown />}
                >
                    Upgrade to {requiredPackage}
                </BTButtonDeprecated>
            );
        }
    };

    return (
        <div className="PackageUpsell">
            {showBanner && (
                <BTRow>
                    <BTCol flex="auto">
                        <BTAlert
                            className="PackageUpsell--BTAlert"
                            type="info"
                            data-testid="upsellPackageAlert"
                            title="Sorry, your account does not have permissions to upgrade."
                            message="Your account isn't configured to allow upgrading. Contact an account admin if that's a mistake."
                            showIcon={true}
                        />
                    </BTCol>
                </BTRow>
            )}
            <BTRow gutter={64}>
                <BTCol xs={12}>
                    {upgradeRequiredInfo.requiredPackage && (
                        <StatusTagDisplay
                            className="PackageUpsell--StatusTag"
                            data-testid="PakageUpsellStatusTagDisplay"
                            statusType="info"
                            statusText={upgradeRequiredInfo.requiredPackage}
                        />
                    )}
                    <BTTitle
                        data-testid="packageUpsellTitle"
                        className="margin-top-md margin-bottom-md"
                        level={1}
                        bold
                    >
                        {title}
                    </BTTitle>
                    <BTTitle className="PackageUpsell--PackageUpsellSubtitle" level={4}>
                        Upgrade to {upgradeRequiredInfo.requiredPackage} for access to this and more
                    </BTTitle>
                    <div className={contentClassName}>{children}</div>
                    {getUpsellButton()}
                </BTCol>
                <BTCol xs={12}>
                    <img
                        className="PackageUpsell--UpsellImage"
                        src={imageSrc}
                        alt="Upsell Illustration"
                    />
                </BTCol>
            </BTRow>
        </div>
    );
};
